/**
 * 精品展示详情页组件
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Button, Spin, Tag, Card } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { showcaseService } from '../../../../services/showcaseService';
import { getCategoryLabel, getIndustryLabel, getTechnologyLabel, getTechnologyLabels } from '../../../../utils/dictConverter';
import ContentRenderer from '../../../../components/common/ContentRenderer';
import './ShowcaseDetailPage.css';

interface ShowcaseDetail {
  id: number;
  title: string;
  description?: string;
  content?: string;
  category?: string;
  image?: string;
  images?: string; // 后端字段：逗号分隔的图片路径
  videoUrl?: string;
  companyName?: string;
  company?: string;
  industry?: string;
  technology?: string;
  keywords?: string[];
  status: number;
  createTime?: string;
  updateTime?: string;
}

const ShowcaseDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  
  const [showcase, setShowcase] = useState<ShowcaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  
  // 获取来源参数，决定返回路径
  const getBackPath = () => {
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get('from');
    
    if (from === 'demand') {
      // 从供需对接平台进入，返回到供需对接平台的专利池页签
      return '/show/demand';
    } else {
      // 从精品展示进入（或没有from参数），返回到精品展示页面
      // 如果展示项是专利池分类，返回到专利池分类页面
      if (showcase?.category === 'patent_pool' || (showcase as any)?.category === '专利池') {
        return '/show/showcase?category=专利池';
      }
      return '/show/showcase';
    }
  };
  
  // 从URL路径中提取ID
  useEffect(() => {
    const loadShowcaseDetail = async () => {
      let id: number = 0;
      const pathname = location.pathname;
      
      // 匹配 /show/showcase/123 格式
      const match = pathname.match(/\/(show\/)?showcase\/(\d+)$/);
      if (match && match[2]) {
        id = parseInt(match[2]);
      } else if (params.id) {
        id = parseInt(params.id);
      }
      
      if (!id || isNaN(id)) {
        setError('无效的展示ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setShowcase(null);

      try {
        const response = await showcaseService.getById(id);
        
        if (response && response.code === 200 && response.data) {
          const data = response.data;
          setShowcase(data as ShowcaseDetail);
        } else {
          setError(response?.message || '展示不存在');
        }
      } catch (apiError: any) {
        if (apiError.response) {
          if (apiError.response.status === 404) {
            setError('展示不存在');
          } else {
            setError(`加载失败: ${apiError.response.data?.message || apiError.message}`);
          }
        } else {
          setError(`加载失败: ${apiError.message || '网络错误'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadShowcaseDetail();
  }, [location.pathname, params.id]);

  // 处理图片路径
  const getImageUrl = (image?: string): string => {
    if (!image) return '';
    const trimmedImage = image.trim();
    // Base64 图片（data:image/...）
    if (trimmedImage.startsWith('data:image/')) {
      return trimmedImage;
    }
    // 完整 URL
    if (trimmedImage.startsWith('http://') || trimmedImage.startsWith('https://')) {
      return trimmedImage;
    }
    // 如果已经是完整路径（以/开头），直接返回
    if (trimmedImage.startsWith('/')) {
      return trimmedImage;
    }
    // 如果是相对路径，添加 /attachments/ 前缀
    return `/attachments/${trimmedImage}`;
  };

  // 清理 base64 图片字符串
  const cleanBase64Image = (imageStr: string): string => {
    if (!imageStr) return '';
    let cleaned = imageStr.trim();
    
    // 如果是 base64 图片（以 data:image 开头），清理并返回
    if (cleaned.startsWith('data:image/')) {
      // 找到 base64, 后面的部分
      const base64Index = cleaned.indexOf('base64,');
      if (base64Index > 0) {
        const prefix = cleaned.substring(0, base64Index + 7); // 包含 'base64,'
        const base64Data = cleaned.substring(base64Index + 7);
        // 移除 base64 数据中的所有空格、换行符、回车符
        // 但保留 = 字符（base64 填充字符）
        const cleanBase64 = base64Data.replace(/[\s\n\r\t]+/g, '');
        
        // 验证 base64 字符串是否有效（只包含 base64 字符）
        const base64Pattern = /^[A-Za-z0-9+/=]+$/;
        if (base64Pattern.test(cleanBase64)) {
          return prefix + cleanBase64;
        } else {
          // 即使有无效字符，也尝试清理后返回
          const validBase64 = cleanBase64.replace(/[^A-Za-z0-9+/=]/g, '');
          return prefix + validBase64;
        }
      }
      // 如果没有 base64, 标记，移除所有空格和换行符
      return cleaned.replace(/[\s\n\r\t]+/g, '');
    }
    
    // 如果只是 base64 编码（没有 data:image 前缀），添加前缀
    if (cleaned.length > 100 && !cleaned.includes('://') && !cleaned.startsWith('/')) {
      // 可能是纯 base64 字符串，尝试添加 data:image/jpeg;base64, 前缀
      const base64Pattern = /^[A-Za-z0-9+/=\s]+$/;
      const cleanBase64 = cleaned.replace(/[\s\n\r\t]+/g, '');
      if (base64Pattern.test(cleaned.substring(0, 100))) {
        return `data:image/jpeg;base64,${cleanBase64}`;
      }
    }
    
    return cleaned;
  };

  // 获取所有图片（images字段可能是逗号分隔的多个 base64 图片）
  const getAllImages = (): string[] => {
    if (!showcase) return [];
    const itemData = showcase as any;
    const images: string[] = [];
    
    // 优先使用 images 字段（后端实际字段）
    if (itemData.images) {
      let imagesStr = typeof itemData.images === 'string' ? itemData.images : String(itemData.images);
      imagesStr = imagesStr.trim();
      
      // 检查是否包含多个 data:image 标记
      const dataImageCount = (imagesStr.match(/data:image\//g) || []).length;
      
      if (dataImageCount > 1) {
        // 多个图片，使用正则表达式精确匹配每张完整的图片
        // 匹配模式：data:image/类型;base64,base64数据（直到下一个data:image/或字符串结束）
        const imagePattern = /data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]+/g;
        const matches: string[] = [];
        let match;
        
        // 先找到所有 data:image/ 的位置
        const dataImageIndices: number[] = [];
        let searchIndex = 0;
        while ((searchIndex = imagesStr.indexOf('data:image/', searchIndex)) !== -1) {
          dataImageIndices.push(searchIndex);
          searchIndex += 11; // 跳过 'data:image/'
        }
        
        // 根据位置索引提取每张图片
        for (let i = 0; i < dataImageIndices.length; i++) {
          const startIndex = dataImageIndices[i];
          const endIndex = i < dataImageIndices.length - 1 
            ? dataImageIndices[i + 1] 
            : imagesStr.length;
          
          const imageStr = imagesStr.substring(startIndex, endIndex);
          
          const cleaned = cleanBase64Image(imageStr);
          if (cleaned && cleaned.length > 100) {
            // 验证 base64 字符串是否以 = 结尾或有足够的长度（至少是有效的 base64）
            const base64Part = cleaned.substring(cleaned.indexOf('base64,') + 7);
            if (base64Part.length > 100) {
              images.push(cleaned);
            }
          }
        }
      } else if (dataImageCount === 1) {
        // 单个图片
        const cleaned = cleanBase64Image(imagesStr);
        if (cleaned) images.push(cleaned);
      } else {
        // 没有 data:image 标记，尝试按逗号分隔
        const parts = imagesStr.split(',').map((img: string) => img.trim()).filter((img: string) => img && img.length > 0);
        parts.forEach((part: string) => {
          const cleaned = cleanBase64Image(part);
          if (cleaned && cleaned.length > 100) {
            images.push(cleaned);
          }
        });
      }
    }
    
    // 兼容 image 字段（单数）
    if (itemData.image) {
      const cleaned = cleanBase64Image(String(itemData.image));
      if (cleaned && !images.includes(cleaned)) {
        images.push(cleaned);
      }
    }
    
    if (showcase?.image) {
      const cleaned = cleanBase64Image(String(showcase.image));
      if (cleaned && !images.includes(cleaned)) {
        images.push(cleaned);
      }
    }
    
    return images;
  };

  // 获取技术标签
  const technologyLabels = useMemo(() => {
    if (!showcase) return [];
    const itemData = showcase as any;
    if (itemData.keywords && Array.isArray(itemData.keywords) && itemData.keywords.length > 0) {
      return itemData.keywords.map((tech: string) => getTechnologyLabel(tech));
    } else if (itemData.technology && typeof itemData.technology === 'string') {
      return getTechnologyLabels(itemData.technology);
    }
    return [];
  }, [showcase]);

  // 加载中状态
  if (loading) {
    return (
      <div className="showcase-detail-page">
        <div className="showcase-detail-content">
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>加载中...</p>
        </div>
      </div>
    );
  }

  // 错误或数据不存在
  if (error || !showcase) {
    const backPath = getBackPath();
    return (
      <div className="showcase-detail-page">
        <div className="showcase-detail-content">
          <Button onClick={() => navigate(backPath)} className="back-button">
            ← 返回列表
          </Button>
          <h1>展示不存在</h1>
          <p>抱歉，您访问的展示不存在。</p>
          <Button onClick={() => navigate(backPath)}>返回列表</Button>
        </div>
      </div>
    );
  }

  const categoryLabel = getCategoryLabel(showcase.category);
  const industryLabel = getIndustryLabel((showcase as any).industry);
  const itemData = showcase as any;
  const backPath = getBackPath();

  return (
    <div className="showcase-detail-page">
      <div className="showcase-detail-content">
        {/* 返回按钮 */}
        <Button 
          onClick={() => navigate(backPath)} 
          className="back-button"
          style={{ marginBottom: '24px' }}
        >
          ← 返回列表
        </Button>

        {/* 展示信息卡片 - 合并展示 */}
        <Card className="showcase-detail-card">
          <div className="showcase-detail-wrapper">
            {/* 头部区域 */}
            <div className="showcase-header-section">
              {/* 标题和标签区域 */}
              <div className="showcase-title-section">
                <div className="showcase-title-row">
                  <h1 className="showcase-name">{showcase.title}</h1>
                  {categoryLabel && (
                    <Tag color="blue" className="showcase-category-tag">
                      {categoryLabel}
                    </Tag>
                  )}
                </div>
                
                {/* 元信息 */}
                <div className="showcase-meta">
                  {(itemData.companyName || itemData.company) && (
                    <div className="meta-item">
                      <span className="meta-label">机构/企业：</span>
                      <span className="meta-value">{itemData.companyName || itemData.company}</span>
                    </div>
                  )}
                  {itemData.industry && (
                    <div className="meta-item">
                      <span className="meta-label">所属行业：</span>
                      <span className="meta-value">{industryLabel || itemData.industry}</span>
                    </div>
                  )}
                </div>
                
                {/* 技术标签 */}
                {technologyLabels.length > 0 && (
                  <div className="showcase-tech-tags">
                    {technologyLabels.map((techLabel: string, idx: number) => (
                      <Tag key={idx} className="tech-tag">{techLabel}</Tag>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 内容区域 */}
            <div className="showcase-content-section">
              {/* 简介 */}
              {showcase.description && (
                <div className="showcase-content-item">
                  <h3 className="showcase-content-title">项目简介</h3>
                  <div className="showcase-content-text">
                    {showcase.description}
                  </div>
                </div>
              )}
              
              {/* 项目展示 - 只有有图片时才显示 */}
              {(() => {
                const allImages = getAllImages();
                if (allImages.length === 0 || imageError) return null;
                
                return (
                  <div className="showcase-content-item">
                    <h3 className="showcase-content-title">项目展示</h3>
                    <div className="showcase-intro-images">
                      {allImages.map((imageUrl, index) => {
                        // 对于 base64 图片，直接使用，不需要 getImageUrl 处理
                        // getImageUrl 可能会添加 /attachments/ 前缀，导致 base64 图片无效
                        const finalUrl = imageUrl.startsWith('data:image/') 
                          ? imageUrl 
                          : getImageUrl(imageUrl);
                        
                        return (
                          <div key={index} className="showcase-intro-image-item">
                            <img 
                              src={finalUrl}
                              alt={`${showcase.title} - 图片${index + 1}`}
                              className="intro-image"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              
              {/* 详细内容 */}
              {showcase.content && (
                <div className="showcase-content-item">
                  <h3 className="showcase-content-title">详细内容</h3>
                  <div className="showcase-content-html">
                    <ContentRenderer content={showcase.content} />
                  </div>
                </div>
              )}
              
              {/* 链接 */}
              {showcase.videoUrl && (
                <div className="showcase-content-item">
                  <h3 className="showcase-content-title">相关视频</h3>
                  <div className="showcase-content-text">
                    <a 
                      href={showcase.videoUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="showcase-link"
                    >
                      {showcase.videoUrl}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 操作按钮 */}
            <div className="showcase-actions">
              <Button 
                size="large"
                onClick={() => navigate(backPath)}
                className="action-button"
              >
                返回列表
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ShowcaseDetailPage;

